/**
 * Load EVERY image in the report — fully, decoded — before it is printed.
 *
 * Port of KinetiQE/src/lib/settleImages.ts, which the kiosk's print window
 * uses; change both together.
 *
 * Printing while photographs were still arriving gave a PDF with blank white
 * figures and half-drawn ones (a phone is the worst case: the print dialog
 * opens at once and the browser prints whatever has painted). So this is
 * strict: each image must have loaded with real pixels (naturalWidth > 0) AND
 * be decoded, so it paints on the first print frame. A failed image is retried
 * with a fresh address; the caller prints only when none are left failing.
 */

const hasPixels = img => img.complete && img.naturalWidth > 0;

/** Resolves true when the image loaded with pixels, false when it errored. */
function waitForLoad(img) {
  if (img.complete) return Promise.resolve(hasPixels(img));
  return new Promise(resolve => {
    img.addEventListener('load', () => resolve(hasPixels(img)), { once: true });
    img.addEventListener('error', () => resolve(false), { once: true });
  });
}

/** The same address, made new, so a failed or truncated response is not served again. */
function reload(img, original, attempt) {
  if (/^(data|blob):/i.test(original)) { img.src = original; return; }
  const joiner = original.includes('?') ? '&' : '?';
  img.src = `${original}${joiner}kq=${Date.now()}-${attempt}`;
}

async function loadOne(img, retries) {
  const original = img.src;
  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt > 0) reload(img, original, attempt);
    // eslint-disable-next-line no-await-in-loop
    if (await waitForLoad(img)) {
      // Decoded now, not on the first print frame: an image that has downloaded
      // but not been decoded can still print as an empty box.
      try { await img.decode?.(); } catch { /* it painted before, or will */ }
      return true;
    }
  }
  return false;
}

/**
 * Resolves { total, loaded, failed }. Images still pending at `timeoutMs` count
 * as failed — the report is not complete.
 */
export async function settleImages(images, { timeoutMs = 60000, retries = 2, onProgress, schedule = setTimeout } = {}) {
  const list = Array.from(images);
  const total = list.length;
  if (!total) return { total: 0, loaded: 0, failed: 0 };

  let loaded = 0;
  const work = Promise.all(list.map(img => loadOne(img, retries).then(ok => {
    if (ok) { loaded++; onProgress?.(loaded, total); }
    return ok;
  })));
  const timeout = new Promise(resolve => { schedule(resolve, timeoutMs); });
  await Promise.race([work, timeout]);

  return { total, loaded, failed: total - loaded };
}
